/**
 * CalendarConnectionItem Component
 * Displays a single calendar connection row with provider info, status, actions,
 * inline display name editing, color picker, and post-OAuth setup expansion.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Calendar, Trash2, RefreshCw, Pencil, Check, X, Star, Loader2 } from 'lucide-react';
import { CalendarConnectionDto, CalendarConnectionStatus, CalendarSyncState, CalendarProvider } from '@signalvote/shared';
import type { CalendarSyncPreferenceDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { ConnectionStatusBadge } from './ConnectionStatusBadge';
import { CalendarColorPicker } from './CalendarColorPicker';
import { useQuery } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import {
  useUpdateCalendarPreference,
} from '@/hooks/queries/useCalendar';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface CalendarConnectionItemProps {
  connection: CalendarConnectionDto;
  isNewConnection?: boolean;
  /** Name of the write calendar on another connection, if one exists */
  existingWriteCalendarName?: string;
  onSetupComplete?: () => void;
  onManageCalendars: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
  onUpdateConnection: (update: { displayName?: string; color?: string }) => void;
  usedColors: Set<string>;
  isDisconnecting?: boolean;
}

// ============================================================================
// Provider Icons (inline SVGs matching ConnectCalendarButton.tsx)
// ============================================================================

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

function ProviderIcon({ provider, className }: { provider: CalendarProvider; className?: string }) {
  switch (provider) {
    case CalendarProvider.Google:
      return <GoogleIcon className={className} />;
    case CalendarProvider.Microsoft:
      return <MicrosoftIcon className={className} />;
    default:
      return <Calendar className={className} />;
  }
}

/**
 * Auto-detect the best default calendar for writes.
 * For Google, the primary calendar's ID matches the account email.
 * Fallback: first calendar in the list.
 */
function detectPrimaryCalendar(
  calendars: CalendarSyncPreferenceDto[],
  accountEmail: string
): CalendarSyncPreferenceDto | undefined {
  // Check if one is already marked primary
  const existing = calendars.find(c => c.isPrimaryWriteCalendar);
  if (existing) return existing;

  // Google names the primary calendar after the email
  const emailMatch = calendars.find(
    c => c.providerCalendarId === accountEmail || c.calendarName === accountEmail
  );
  if (emailMatch) return emailMatch;

  // Fallback: first calendar
  return calendars[0];
}

// ============================================================================
// Setup Panel Component (inline expansion after OAuth)
// ============================================================================

interface SetupPanelProps {
  connectionId: string;
  accountEmail: string;
  /** Name of the write calendar already set on another connection */
  existingWriteCalendarName?: string;
  onComplete: () => void;
}

function SetupPanel({ connectionId, accountEmail, existingWriteCalendarName, onComplete }: SetupPanelProps) {
  // Use direct query with refetchInterval to poll until calendars appear
  const { data: calendars, isLoading } = useQuery({
    queryKey: queryKeys.calendar.calendars(connectionId),
    queryFn: () => getApiClient().calendar.getCalendars(connectionId),
    enabled: !!connectionId,
    refetchInterval: (query) => {
      // Poll every 2s until calendars appear, then stop
      const data = query.state.data;
      return !data || data.length === 0 ? 2000 : false;
    },
  });
  const { mutateAsync: updatePreference } = useUpdateCalendarPreference();

  const hasCalendars = calendars && calendars.length > 0;
  const hasExistingWrite = !!existingWriteCalendarName;

  // When another connection already has a write calendar, default to "read only"
  const [wantsWrite, setWantsWrite] = useState(!hasExistingWrite);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Auto-select best calendar when calendars load (only if user wants to write)
  const autoDetected = useMemo(() => {
    if (!calendars || calendars.length === 0) return undefined;
    return detectPrimaryCalendar(calendars, accountEmail);
  }, [calendars, accountEmail]);

  useEffect(() => {
    if (autoDetected && !selectedId && wantsWrite) {
      setSelectedId(autoDetected.providerCalendarId);
    }
  }, [autoDetected, selectedId, wantsWrite]);

  const handleConfirm = useCallback(async () => {
    if (!wantsWrite) {
      // User chose read-only — just close the panel
      onComplete();
      return;
    }
    if (!selectedId || !connectionId) return;
    setIsConfirming(true);
    try {
      await updatePreference({
        connectionId,
        calendarId: selectedId,
        request: { isPrimaryWriteCalendar: true },
      });
      onComplete();
    } catch {
      setIsConfirming(false);
    }
  }, [wantsWrite, selectedId, connectionId, updatePreference, onComplete]);

  // Loading state — waiting for calendars to sync
  if (isLoading || !hasCalendars) {
    return (
      <div className="mt-3 rounded-lg border border-dashed border-border bg-muted/30 p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Fetching your calendars...
        </div>
      </div>
    );
  }

  // Another connection already has a write calendar — ask before enabling writes here too
  if (hasExistingWrite) {
    return (
      <div className="mt-3 rounded-lg border border-border bg-muted/30 p-4">
        <p className="text-sm font-medium mb-1">
          Already writing to {existingWriteCalendarName}
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          Want to also write shifts & tasks to this account?
        </p>

        <div className="space-y-1.5 mb-3">
          <label
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
              !wantsWrite
                ? 'bg-primary/10 border border-primary/30'
                : 'hover:bg-accent border border-transparent'
            )}
          >
            <input
              type="radio"
              name="writeChoice"
              checked={!wantsWrite}
              onChange={() => { setWantsWrite(false); setSelectedId(null); }}
              className="accent-primary"
            />
            <span className="text-sm">No, just read events</span>
          </label>
          <label
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
              wantsWrite
                ? 'bg-primary/10 border border-primary/30'
                : 'hover:bg-accent border border-transparent'
            )}
          >
            <input
              type="radio"
              name="writeChoice"
              checked={wantsWrite}
              onChange={() => {
                setWantsWrite(true);
                if (autoDetected) setSelectedId(autoDetected.providerCalendarId);
              }}
              className="accent-primary"
            />
            <span className="text-sm">Yes, also write here</span>
          </label>
        </div>

        {/* Calendar picker — only shown when user opts in to write */}
        {wantsWrite && (
          <div className="space-y-1.5 ml-6 mb-3">
            {calendars.map((cal) => (
              <label
                key={cal.providerCalendarId}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
                  selectedId === cal.providerCalendarId
                    ? 'bg-primary/10 border border-primary/30'
                    : 'hover:bg-accent border border-transparent'
                )}
              >
                <input
                  type="radio"
                  name="writeCalendar"
                  value={cal.providerCalendarId}
                  checked={selectedId === cal.providerCalendarId}
                  onChange={() => setSelectedId(cal.providerCalendarId)}
                  className="accent-primary"
                />
                <div
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cal.color || cal.providerColor || '#9ca3af' }}
                />
                <span className="text-sm truncate">{cal.calendarName}</span>
              </label>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={wantsWrite && !selectedId || isConfirming}
          >
            {isConfirming ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
          <button
            type="button"
            onClick={onComplete}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            I'll do this later
          </button>
        </div>
      </div>
    );
  }

  // First connection (no existing write calendar) — standard setup
  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/30 p-4">
      <p className="text-sm font-medium mb-3">
        Almost done! Pick a calendar for shifts & tasks
      </p>

      <div className="space-y-1.5">
        {calendars.map((cal) => (
          <label
            key={cal.providerCalendarId}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
              selectedId === cal.providerCalendarId
                ? 'bg-primary/10 border border-primary/30'
                : 'hover:bg-accent border border-transparent'
            )}
          >
            <input
              type="radio"
              name="writeCalendar"
              value={cal.providerCalendarId}
              checked={selectedId === cal.providerCalendarId}
              onChange={() => setSelectedId(cal.providerCalendarId)}
              className="accent-primary"
            />
            <div
              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: cal.color || cal.providerColor || '#9ca3af' }}
            />
            <span className="text-sm truncate">{cal.calendarName}</span>
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={!selectedId || isConfirming}
        >
          {isConfirming ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Saving...
            </>
          ) : (
            'Confirm'
          )}
        </Button>
        <button
          type="button"
          onClick={onComplete}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          I'll do this later
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function CalendarConnectionItem({
  connection,
  isNewConnection = false,
  existingWriteCalendarName,
  onSetupComplete,
  onManageCalendars,
  onReconnect,
  onDisconnect,
  onUpdateConnection,
  usedColors,
  isDisconnecting = false,
}: CalendarConnectionItemProps) {
  // Any non-Active status means the connection is broken (token expired, access revoked,
  // or a permanent provider error such as a deleted/404 calendar) and can only be fixed by
  // re-linking — so offer Reconnect for all of them, not just TokenExpired.
  const showReconnect = connection.status !== CalendarConnectionStatus.Active;
  const displayName = connection.displayName || connection.accountEmail;
  const hasCustomName = !!connection.displayName;

  // Determine if setup panel should show:
  // Show for new connections that don't already have a primary write calendar
  const hasWriteCalendar = connection.syncPreferences?.some(p => p.isPrimaryWriteCalendar);
  const showSetup = isNewConnection && !hasWriteCalendar;

  // Inline edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleStartEdit = useCallback(() => {
    setEditValue(connection.displayName || '');
    setIsEditing(true);
    // Focus will happen after render
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [connection.displayName]);

  const handleSaveEdit = useCallback(() => {
    const trimmed = editValue.trim();
    // If empty, clear display name (revert to email)
    const newName = trimmed || undefined;
    if (newName !== (connection.displayName || undefined)) {
      onUpdateConnection({ displayName: newName ?? '' });
    }
    setIsEditing(false);
  }, [editValue, connection.displayName, onUpdateConnection]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleColorSelect = useCallback(
    (color: string) => {
      onUpdateConnection({ color });
    },
    [onUpdateConnection]
  );

  const writeCalendar = connection.syncPreferences?.find(p => p.isPrimaryWriteCalendar);

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-white transition-colors',
        !showSetup && 'hover:bg-gray-50'
      )}
    >
      {/* Card header — always visible */}
      <div className="flex items-center justify-between gap-4 p-4">
        {/* Left side: Provider icon, color dot, name, and status */}
        <div className="flex items-center gap-3 min-w-0">
          {/* Provider icon */}
          <div className="flex-shrink-0">
            <ProviderIcon provider={connection.provider} className="h-6 w-6" />
          </div>

          {/* Color picker */}
          <div className="flex-shrink-0">
            <CalendarColorPicker
              selectedColor={connection.color}
              usedColors={usedColors}
              onColorSelect={handleColorSelect}
            />
          </div>

          {/* Account info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    onBlur={handleSaveEdit}
                    placeholder={connection.accountEmail}
                    className="h-6 rounded border border-input px-1.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-ring"
                    maxLength={100}
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
                    onClick={handleSaveEdit}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-accent text-primary"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleCancelEdit}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-accent text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleStartEdit}
                  className="group flex items-center gap-1 text-sm font-medium text-gray-900 truncate hover:text-primary transition-colors"
                  title="Click to rename"
                >
                  <span className="truncate">{displayName}</span>
                  <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                </button>
              )}
              <ConnectionStatusBadge status={connection.status} />
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {hasCustomName && (
                <span className="text-muted-foreground mr-2">{connection.accountEmail}</span>
              )}
              {connection.syncState === CalendarSyncState.Syncing ? (
                <span className="inline-flex items-center gap-1 text-primary">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Syncing…
                </span>
              ) : (
                <>Last synced: {formatRelativeTime(connection.lastSyncAt)}</>
              )}
            </div>
            {/* Write status indicator — only on the connection that has one */}
            {!showSetup && writeCalendar && (
              <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Star className="h-3 w-3 fill-current text-warning" />
                Writes to: {writeCalendar.calendarName}
              </span>
            )}
          </div>
        </div>

        {/* Right side: Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onManageCalendars}
            className="gap-1.5"
          >
            <Calendar className="h-4 w-4" />
            Calendars
          </Button>

          {showReconnect && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReconnect}
              className="gap-1.5"
            >
              <RefreshCw className="h-4 w-4" />
              Reconnect
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={onDisconnect}
            disabled={isDisconnecting}
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Disconnect</span>
          </Button>
        </div>
      </div>

      {/* Inline setup expansion — shown for new connections without a write calendar */}
      {showSetup && onSetupComplete && (
        <div className="px-4 pb-4">
          <SetupPanel
            connectionId={connection.id}
            accountEmail={connection.accountEmail}
            existingWriteCalendarName={existingWriteCalendarName}
            onComplete={onSetupComplete}
          />
        </div>
      )}
    </div>
  );
}

export default CalendarConnectionItem;
